/**
 * @module CustomerRevenueService
 * @remarks RTK Query service for customer revenue data
 */

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { CustomerRevenue } from "@models/customer-revenue.model";

const dbName = process.env.NEXT_PUBLIC_API_URL === "https://khg-ae.dokploy.dev.q23.de/index.php/" ? "KundenumsätzeKHG" : "Kundenumsätze";

/**
 * API service for managing customer revenue data
 */
export const customerRevenueApi = createApi({
  reducerPath: "customerRevenueApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/",
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("auth_token");
      if (token) {
        headers.set("Authorization", token);
      }
      return headers;
    },
  }),
  tagTypes: ["CustomerRevenue"],
  endpoints: (builder) => ({
    getCustomerRevenue: builder.query<CustomerRevenue[], { customer_number: string; page?: number; pageSize?: number }>({
      query: ({ customer_number, page = 1, pageSize = 10 }) => {
        const offset = (page - 1) * pageSize;
        
        return {
          url: "data/kundenumsätze",
          method: "POST",
          body: {
            db: dbName,
            where: {
              "kunde.kundennummer": customer_number,
            },
            limit: pageSize,
            offset: offset,
            fields: []
          },
        };
      },
      transformResponse: (response: CustomerRevenue[] | CustomerRevenue) => {
        console.log('API Raw Response type:', Array.isArray(response) ? 'array' : typeof response);
        
        // Handle both array and single object responses
        if (!response) {
          console.log('Empty response received');
          return [];
        }
        
        // If it's a single object (not in an array), wrap it in an array
        if (!Array.isArray(response) && typeof response === 'object') {
          console.log('Single object response detected, converting to array');
          return [response];
        }
        
        // Otherwise, return the array as is
        console.log('Array response with length:', Array.isArray(response) ? response.length : 'not an array');
        return response as CustomerRevenue[];
      },
      providesTags: ["CustomerRevenue"],
      // Zwischenspeichern für einen Tag (86.400 Sekunden)
      keepUnusedDataFor: 86400,
    }),
    invalidateCustomerRevenue: builder.mutation<void, void>({
      queryFn: () => ({ data: undefined }),
      invalidatesTags: ["CustomerRevenue"],
    }),
  }),
});

// Export hooks für Verwendung in Komponenten
export const {
  useGetCustomerRevenueQuery,
  useInvalidateCustomerRevenueMutation,
} = customerRevenueApi;
